import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { AddressModel, CarrierModel, SetInitialStateFacade } from '../../../../../../common/models';
import {
  CalendarBasedUliCreationFormModel,
  CreateAndAllocateUliRequestModel,
  SlotCompactInfo,
  UliCreateSelectionModel,
} from '../../models';
import { ActiveCompartmentsModel, UliFormModel, UliLoadSearchModel, UliRule } from '../models';
import { UliDefaultHubTruckMappingModel } from '../models/uli-default-hub-truck-mapping.model';
import {
  MultiStopUliCreate,
  SetUliRule,
  UliClearLoadSearch,
  UliCreate,
  UliCreateAndAllocate,
  UliCreationCarriersLoad,
  UliCreationClearStorage,
  UliCreationCompartmentListLoad,
  UliCreationDefaultHubTruckMappingLoad,
  UliCreationDeliveryPartyAddressesLoad,
  UliCreationHubTruckMappingLoad,
  UliCreationInitialState,
  UliCreationSetCalendarPrefill,
  UliCreationSetPrefill,
  UliLoadSearch,
} from './uli-creation.actions';
import { UliCreationState } from './uli-creation.state';
import { MultiStopUliFormModel } from '../models/uli-multi-stop-creation.model';
import { HubTrucksModel } from '../../../../administration';

@Injectable({
  providedIn: 'root',
})
export class UliCreationFacade implements SetInitialStateFacade {
  compartments$: Observable<ActiveCompartmentsModel[]> = this.store.select(UliCreationState.getCompartments);
  deliveryPartyAddresses$: Observable<AddressModel[]> = this.store.select(UliCreationState.getPartyDeliveryAddresses);
  canLoadMoreDeliveryPartyAddress$: Observable<boolean> = this.store.select(
    UliCreationState.isMoreDeliveryAddressesLoadable,
  );
  carriers$: Observable<CarrierModel[]> = this.store.select(UliCreationState.getCarriers);
  canLoadMoreCarriers$: Observable<boolean> = this.store.select(UliCreationState.isMoreCarriersLoadable);
  loadSearchResult$: Observable<UliLoadSearchModel> = this.store.select(UliCreationState.getLoadSearchResult);
  uliRule$: Observable<UliRule> = this.store.select(UliCreationState.getUliRule);
  slot$: Observable<SlotCompactInfo> = this.store.select(UliCreationState.getSlot);
  prefillData$: Observable<UliCreateSelectionModel> = this.store.select(UliCreationState.getPrefillData);
  calendarPrefillData$: Observable<CalendarBasedUliCreationFormModel> = this.store.select(
    UliCreationState.getCalendarPrefillData,
  );
  defaultHubTruckMapping$: Observable<UliDefaultHubTruckMappingModel> = this.store.select(
    UliCreationState.getDefaultHubTruckMapping,
  );
  hubTruckMapping$: Observable<HubTrucksModel> = this.store.select(UliCreationState.getHubTruckMapping);

  constructor(private store: Store) {}

  getCompartments(hubId: number): void {
    this.store.dispatch(new UliCreationCompartmentListLoad(hubId));
  }

  getHubTruckMapping(hubId: number): void {
    this.store.dispatch(new UliCreationHubTruckMappingLoad(hubId));
  }

  getDefaultHubTruckMapping(hubId: number): void {
    this.store.dispatch(new UliCreationDefaultHubTruckMappingLoad(hubId));
  }

  getDeliveryPartyAddresses(searchStr: string, limit?: number): void {
    this.store.dispatch(new UliCreationDeliveryPartyAddressesLoad({ filter: searchStr, limit }));
  }

  getCarriers(searchStr: string, limit?: number): void {
    this.store.dispatch(new UliCreationCarriersLoad({ filter: searchStr, limit }));
  }

  createUli(data: UliFormModel): void {
    this.store.dispatch(new UliCreate(data));
  }

  createAndAllocateUli(data: CreateAndAllocateUliRequestModel): void {
    this.store.dispatch(new UliCreateAndAllocate(data));
  }

  createMultiStopUli(data: MultiStopUliFormModel): void {
    this.store.dispatch(new MultiStopUliCreate(data));
  }

  createPrefillData(data: UliCreateSelectionModel): void {
    this.store.dispatch(new UliCreationSetPrefill(data));
  }

  createCalendarPrefillData(data: CalendarBasedUliCreationFormModel): void {
    this.store.dispatch(new UliCreationSetCalendarPrefill(data));
  }

  loadSearch(loadNumber: string): void {
    this.store.dispatch(new UliLoadSearch({ loadNumber }));
  }

  clearLoadSearch(): void {
    this.store.dispatch(new UliClearLoadSearch());
  }

  clearPrefillDataFromStorage(): void {
    this.store.dispatch(new UliCreationClearStorage());
  }

  setUliRule(uliRule: UliRule): void {
    this.store.dispatch(new SetUliRule(uliRule));
  }

  setInitialState(): void {
    this.store.dispatch(new UliCreationInitialState());
  }
}
